(ns insta.components
  (:require ["./web_component" :as wc]
            ["fabric" :refer [fabric]]
            [insta.render :as render]
            [storrito.editor.poll-v2-sticker :as poll-v2-sticker]
            [storrito.editor.sticker :as pill-sticker]
            [storrito.editor.icon-sticker :as icon-sticker]
            [storrito.editor.sticker-design :as designs]
            [storrito.editor.countdown-sticker :as countdown-sticker]
            [storrito.editor.question-sticker :as question-sticker]
            [storrito.editor.quiz-sticker :as quiz-sticker]
            [storrito.editor.emoji-slider-sticker :as emoji-slider]
            [cljs-time.coerce :as tc]
            [clojure.string :as str]))

;; Concept:
;;
;; Provides <insta-*> web components that render Instagram stickers using
;; the same FabricJS code as editor2. Each sticker gets its own HTML5
;; canvas inside a Shadow DOM. This reuses existing rendering code so
;; stickers only need to be maintained once.
;;
;; <insta-story> accepts an optional `src` attribute for the main video.
;; The caller is responsible for providing a fully rendered video file —
;; GIFs, animated images, or other non-video formats are not supported.
;; The default background is black (#000). Use a child <div> or <img>
;; element with absolute positioning to set a custom background color or
;; image. The `src` video can be toggled play/pause by clicking the story.

(def ^:private question-colors
  {"white" "#ffffff" "black" "#262626" "pink" "#FE0169"
   "lavender" "#D402C6" "purple" "#7536FA" "orange" "#FF7A00"
   "green" "#1DD04F" "blue" "#0294F6"})

(defonce ^:private fonts-injected? (atom false))
(defonce ^:private google-fonts-loaded (atom nil))

(defn- inject-fonts! []
  (when-not @fonts-injected?
    (reset! fonts-injected? true)
    ;; Load /css/fonts.css which contains all Google Fonts imports and
    ;; local @font-face declarations. Capture its load promise so we can
    ;; wait for the stylesheet to be fully parsed before calling
    ;; document.fonts.load().
    (let [link (js/document.createElement "link")
          p (js/Promise.
              (fn [resolve]
                (.addEventListener link "load" resolve #js {:once true})
                (.addEventListener link "error" resolve #js {:once true})))]
      (reset! google-fonts-loaded p)
      (set! (.-rel link) "stylesheet")
      (set! (.-href link) (str (or designs/script-origin "") "/css/fonts.css"))
      (.appendChild js/document.head link))))

(defn- icon-images-ready []
  (js/Promise.all
    (into-array
      (map (fn [[_ img]]
             (if (.-complete img)
               (js/Promise.resolve)
               (js/Promise.
                 (fn [resolve]
                   (.addEventListener img "load" resolve #js {:once true})
                   (.addEventListener img "error" resolve #js {:once true})))))
           designs/icon-map))))

(defn- load-fonts!
  "Waits for the Google Fonts stylesheet to load, then explicitly
   triggers font downloads for all fonts used by stickers."
  []
  (-> @google-fonts-loaded
      (.then
        (fn []
          (js/Promise.all
            #js [(.load js/document.fonts "normal 100px 'Instagram Sans Condensed'")
                 (.load js/document.fonts "bold 100px 'Instagram Sans Condensed'")
                 (.load js/document.fonts "normal 100px 'AvenyT'")
                 (.load js/document.fonts "normal 100px 'Roboto'")
                 (.load js/document.fonts "bold 100px 'Roboto'")
                 (.load js/document.fonts "normal 100px 'Noto Color Emoji'")])))))

(defonce ^:private ready-promise
  (do
    (inject-fonts!)
    (js/Promise.all
      #js [(load-fonts!)
           (icon-images-ready)])))

(defn- with-ready [f]
  (.then ready-promise f))

(def ^:private story-width 1080)
(def ^:private story-height 1920)

(defn- fit-scale
  "Returns a uniform scale factor (capped at 1.0) that ensures
   the given dimensions fit within 1080x1920."
  [w h]
  (min 1.0
       (/ story-width w)
       (/ story-height h)))

(defn- render-fabric-object!
  "Creates a FabricJS StaticCanvas, renders the given object onto it,
   and returns {:canvas-el html-element, :fabric-canvas fabric-instance}.
   Objects wider than 1080px or taller than 1920px are scaled down to fit."
  [fabric-object]
  (let [w (.-width fabric-object)
        h (.-height fabric-object)
        scale (fit-scale w h)
        canvas-w (js/Math.ceil (* w scale))
        canvas-h (js/Math.ceil (* h scale))
        canvas-el (js/document.createElement "canvas")]
    (set! (.-width canvas-el) canvas-w)
    (set! (.-height canvas-el) canvas-h)
    (let [fabric-canvas (js/fabric.StaticCanvas. canvas-el)]
      (set! (.-preserveObjectStacking fabric-canvas) true)
      (when (< scale 1.0)
        (.set fabric-object #js {:scaleX scale :scaleY scale}))
      (.add fabric-canvas fabric-object)
      (.renderAll fabric-canvas)
      {:canvas-el canvas-el :fabric-canvas fabric-canvas})))

(defn- dispose-fabric-canvas! [^js el]
  (when-let [fc (.-_fabricCanvas el)]
    (.dispose fc)
    (set! (.-_fabricCanvas el) nil)))

(defn- set-shadow-canvas!
  "Replaces the shadow DOM content with the rendered FabricJS object.
   The canvas is rendered at full resolution and CSS-scaled to fit the
   host element's available space via max-width:100%."
  [^js el fabric-object]
  (dispose-fabric-canvas! el)
  ;; A sticker that was invisible before its design changed:
  (when (.-_invisibleDisplay el)
    (set! (.. el -style -display) "")
    (set! (.-_invisibleDisplay el) nil))
  (let [shadow (.-shadowRoot el)
        {:keys [canvas-el fabric-canvas]} (render-fabric-object! fabric-object)]
    (set! (.-innerHTML shadow) "")
    ;; Responsive canvas scaling — the canvas bitmap is rendered at full
    ;; resolution (up to 1080px wide via fit-scale), then CSS scales it
    ;; down to fit the host element's actual laid-out width, like a
    ;; responsive <img>. This lets flex layout naturally distribute space
    ;; when multiple stickers share a row — each shrinks proportionally.
    ;;
    ;; - max-width:100% on canvas — never wider than the host element
    ;; - height:auto on canvas    — maintain aspect ratio when scaled
    ;; - min-width:0 on host      — allow flex to shrink below content
    ;;   size (by default flex items won't shrink past their content)
    (set! (.. canvas-el -style -display) "block")
    (set! (.. canvas-el -style -maxWidth) "100%")
    (set! (.. canvas-el -style -height) "auto")
    (set! (.. el -style -minWidth) "0")
    (.appendChild shadow canvas-el)
    (set! (.-_fabricCanvas el) fabric-canvas)))


(defn- invisible?
  [^js el]
  ;; In any case, like the server's `invisible-design?`:
  (= "invisible" (some-> (.getAttribute el "design") str/trim str/lower-case)))

(defn- sticker-design
  "The design the element's `design` attribute names: one of
   `named-designs`, the invisible design, or the default."
  [^js el invisible named-designs default-design invisible-design]
  (if invisible
    invisible-design
    (designs/find-design-by-name named-designs
                                 (or (.getAttribute el "design") "default")
                                 default-design)))

(defn- set-shadow-invisible!
  "An invisible sticker (`design=\"invisible\"`): Instagram gets a
   tappable area without a visual, so the page only shows a dashed frame
   where the area is, its text small in the corner so that what it
   covers stays readable. The frame fills the host element, whose CSS
   `width` and `height` set the area; without them it has the size of
   the visible sticker. The transparent canvas under the frame gives the
   host that size and the extraction its measurements."
  [^js el fabric-object label]
  (dispose-fabric-canvas! el)
  (let [shadow (.-shadowRoot el)
        {:keys [canvas-el fabric-canvas]} (render-fabric-object! fabric-object)
        box (js/document.createElement "div")
        frame (js/document.createElement "div")]
    (set! (.-innerHTML shadow) "")
    (set! (.. box -style -cssText)
          "position:relative;width:100%;height:100%")
    (set! (.. canvas-el -style -cssText)
          "display:block;width:100%;height:100%;opacity:0")
    (set! (.. frame -style -cssText)
          (str "position:absolute;inset:0;box-sizing:border-box;"
               "border:7px dashed rgba(255,255,255,0.8);border-radius:10px;"
               "background:rgba(0,0,0,0.25);padding:6px 14px;overflow:hidden;"
               "white-space:nowrap;text-overflow:ellipsis;color:#ffffff;"
               "font:28px 'Instagram Sans Condensed'"))
    (set! (.-textContent frame) label)
    ;; An inline element ignores CSS width and height. Before the element
    ;; is connected its computed display is "", so the render on connect
    ;; is the one that switches it:
    (when (= "inline" (.-display (js/getComputedStyle el)))
      (set! (.. el -style -display) "inline-block")
      (set! (.-_invisibleDisplay el) true))
    (set! (.. el -style -minWidth) "0")
    (.appendChild box canvas-el)
    (.appendChild box frame)
    (.appendChild shadow box)
    (set! (.-_fabricCanvas el) fabric-canvas)))

(defn- define-sticker!
  "Defines a custom element for a sticker type. The render-fn receives the
   host element and should call set-shadow-canvas! with a FabricJS object.
   Positioning is left to the consumer via CSS."
  [tag-name sticker-attrs render-fn]
  (let [cls (wc/createComponent (clj->js sticker-attrs))]
    (set! (.. cls -prototype -_onConnect)
          (fn []
            (this-as el
              (with-ready
                (fn []
                  (try
                    (render-fn el)
                    (catch js/Error e
                      (js/console.error (str "Error rendering <" tag-name ">:") e))))))))
    (set! (.. cls -prototype -_onAttrChange)
          (fn [_ _ _]
            (this-as el
              (with-ready
                #(try
                   (render-fn el)
                   (catch js/Error e
                     (js/console.error (str "Error rendering <" tag-name ">:") e)))))))
    (set! (.. cls -prototype -_onDisconnect)
          (fn []
            (this-as el
              (dispose-fabric-canvas! el))))
    (js/customElements.define tag-name cls)))

(defn- render-story! [el]
  (let [shadow (.-shadowRoot el)
        src (.getAttribute el "src")]
    ;; Set dimensions directly on host element so external layout cannot
    ;; collapse them (`:host` CSS has very low specificity).
    (let [style (.-style el)]
      (set! (.-display style) "block")
      (set! (.-position style) "relative")
      (set! (.-width style) "1080px")
      (set! (.-height style) "1920px")
      (set! (.-overflow style) "hidden")
      (set! (.-flexShrink style) "0"))
    ;; Build DOM via createElement to avoid XSS from attribute values.
    (set! (.-innerHTML shadow) "")
    (let [style-el (js/document.createElement "style")]
      (set! (.-textContent style-el)
            (str ".story { position: relative; width: 1080px; height: 1920px; overflow: hidden; background: #000; }"
                 ".bg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }"
                 ".content { position: relative; z-index: 1; width: 100%; height: 100%; }"))
      (.appendChild shadow style-el))
    (let [story-div (js/document.createElement "div")]
      (.add (.-classList story-div) "story")
      (when src
        (let [v (js/document.createElement "video")]
          (.add (.-classList v) "bg")
          (set! (.-src v) src)
          (.setAttribute v "playsinline" "")
          (.appendChild story-div v)
          (.addEventListener story-div "click"
            (fn []
              (if (.-paused v)
                (.play v)
                (.pause v))))))
      (let [content-div (js/document.createElement "div")]
        (.add (.-classList content-div) "content")
        (.appendChild content-div (js/document.createElement "slot"))
        (.appendChild story-div content-div))
      (.appendChild shadow story-div))))

(defn- define-story! []
  (let [cls (wc/createComponent #js ["src"])]
    (set! (.. cls -prototype -_onConnect)
          (fn []
            (this-as el (render-story! el))))
    (set! (.. cls -prototype -_onAttrChange)
          (fn [_ _ _]
            (this-as el (render-story! el))))
    (js/customElements.define "insta-story" cls)))

(defn- render-hashtag! [el]
  (let [tag (or (.getAttribute el "hashtag") "hashtag")
        invisible (invisible? el)
        design (sticker-design el invisible designs/new-hashtag-designs designs/default-hashtag-design
                               designs/invisible-tag-design)
        sticker (pill-sticker/create-sticker
                  {:text (str "#" tag)
                   :scaleX 1 :scaleY 1 :top 0 :left 0
                   :type "hashtag"
                   :design design})]
    (if invisible
      (set-shadow-invisible! el sticker (str "#" tag))
      (set-shadow-canvas! el sticker))))

(defn- render-mention! [el]
  (let [username (or (.getAttribute el "username") "username")
        invisible (invisible? el)
        design (sticker-design el invisible designs/new-mention-designs designs/default-mention-design
                               designs/invisible-tag-design)
        sticker (pill-sticker/create-sticker
                  {:text (str "@" username)
                   :scaleX 1 :scaleY 1 :top 0 :left 0
                   :type "usertag"
                   :design design})]
    (if invisible
      (set-shadow-invisible! el sticker (str "@" username))
      (set-shadow-canvas! el sticker))))

(defn- render-location! [el]
  (let [location-name (or (.getAttribute el "location") "Location")
        design (designs/find-design-by-name designs/new-location-designs
                                           (or (.getAttribute el "design") "default")
                                           designs/default-location-design)]
    (set-shadow-canvas! el
      (icon-sticker/icon-sticker
        {:text location-name
         :location {:name location-name
                    :external_id (or (.getAttribute el "location-id") "")}
         :design design
         :id "location-sticker"
         :scaleX 1 :scaleY 1 :top 0 :left 0}))))

(defn- render-link! [el]
  (let [text (or (.getAttribute el "text") "Link")
        url (.getAttribute el "url")
        invisible (invisible? el)
        design (sticker-design el invisible designs/new-link-designs designs/default-link-design
                               designs/invisible-link-design)
        sticker (icon-sticker/icon-sticker
                  {:text text
                   :link url
                   :design design
                   :id "link-sticker"
                   :scaleX 1 :scaleY 1 :top 0 :left 0})]
    (if invisible
      ;; The frame's label is the link's own text, else its URL, as in the
      ;; server's warnings (`text` above defaults to "Link" for the pill):
      (set-shadow-invisible! el sticker (or (.getAttribute el "text") url "Link"))
      (set-shadow-canvas! el sticker))))

(defn- render-poll! [el]
  (let [question (or (.getAttribute el "question") "Ask a question...")
        color-name (or (.getAttribute el "color") "black")
        options-json (or (.getAttribute el "options") "[\"Yes\", \"No\"]")
        options (try (js->clj (js/JSON.parse options-json))
                     (catch :default _ ["Yes" "No"]))]
    (set-shadow-canvas! el
      (poll-v2-sticker/sticker
        {:question question
         :tallies (mapv (fn [text] {:text text :font_size 42}) options)
         :color color-name
         :top 0 :left 0}))))

(defn- render-question! [el]
  (let [question (or (.getAttribute el "question") "Ask me a question")
        color-name (or (.getAttribute el "color") "white")
        bg-color (get question-colors color-name color-name)
        text-color (if (#{"white" "#ffffff"} color-name) "#000000" "#ffffff")]
    (set-shadow-canvas! el
      (question-sticker/question-sticker
        {:question question
         :background-color bg-color
         :text-color text-color
         :top 0 :left 0 :scaleX 1 :scaleY 1}))))

(defn- render-quiz! [el]
  (let [question (or (.getAttribute el "question") "Quiz question?")
        correct-answer (js/parseInt (or (.getAttribute el "correct-answer") "0") 10)
        options-json (or (.getAttribute el "options") "[\"Option A\", \"Option B\"]")
        options (try (js->clj (js/JSON.parse options-json))
                     (catch :default _ ["Option A" "Option B"]))]
    (set-shadow-canvas! el
      (quiz-sticker/quiz-sticker
        {:question question
         :options options
         :correct_answer correct-answer
         :start_background_color "#262626"
         :end_background_color "#262626"
         :text_color "#ffffff"
         :top 0 :left 0 :scaleX 1 :scaleY 1}))))

(defn- render-countdown! [el]
  (let [title (or (.getAttribute el "title") "COUNTDOWN")
        end-time-str (.getAttribute el "end-time")
        end-date (when end-time-str
                   (let [d (js/Date. end-time-str)]
                     (when-not (js/isNaN (.getTime d)) d)))
        end-ts (tc/from-date (or end-date
                                 (js/Date. (+ (js/Date.now) 86400000))))
        color-name (or (.getAttribute el "color") "black")
        colors (get designs/countdown-colors color-name designs/countdown-default-color)]
    (set-shadow-canvas! el
      (countdown-sticker/countdown-sticker
        (merge colors
               {:text title
                :end_ts end-ts
                :top 0 :left 0 :scaleX 1 :scaleY 1})))))

(defn- render-emoji-slider! [el]
  (let [question (or (.getAttribute el "question") "")
        emoji (or (.getAttribute el "emoji") "\uD83D\uDE0D")
        bg-color (or (.getAttribute el "color") "#ffffff")
        text-color (or (.getAttribute el "text-color") "#000000")]
    (set-shadow-canvas! el
      (emoji-slider/slider-sticker
        {:question question
         :background-color bg-color
         :text-color text-color
         :emoji emoji
         :top 0 :left 0}))))

(defn init! []
  (when-not (js/customElements.get "insta-story")
    (define-story!)
    (define-sticker! "insta-hashtag"
      ["hashtag" "design"]
      render-hashtag!)
    (define-sticker! "insta-mention"
      ["username" "design"]
      render-mention!)
    (define-sticker! "insta-location"
      ["location" "location-id" "design"]
      render-location!)
    (define-sticker! "insta-link"
      ["url" "text" "design"]
      render-link!)
    (define-sticker! "insta-poll"
      ["question" "options" "color"]
      render-poll!)
    (define-sticker! "insta-question"
      ["question" "color"]
      render-question!)
    (define-sticker! "insta-quiz"
      ["question" "options" "correct-answer"]
      render-quiz!)
    (define-sticker! "insta-countdown"
      ["title" "end-time" "color"]
      render-countdown!)
    (define-sticker! "insta-emoji-slider"
      ["question" "emoji" "color" "text-color"]
      render-emoji-slider!)
    (set! js/window.__INSTA_COMPONENTS_READY__ ready-promise)
    (render/init!)))
